import { reportErrorToBugsnag } from '@air/utils-error';

import {
  type AssetCustomFieldValueUpdateTask,
  type AssetRestorationTask,
  type AssetTaggingTask,
  type AssetTransformationTask,
  type BoardDeletionTask,
  type BoardRestorationTask,
  type CTWTask,
  type DuplicateBoardTask,
  type ImporterTask,
  type LibraryDeletionTask,
  type LibraryRestorationTask,
  type Task,
  type ZippingTask,
} from '~/store/tasks/types';
import { type MetaImportDescriptor } from '~/swr-hooks/ads/useStartImportAssetFromAd';

export const isTaskZippingTask = (task: Pick<Task, 'type'>): task is ZippingTask => task.type === 'ZippingForDownload';

export const getZippingTasksFromTasks = (tasks: Task[]) =>
  tasks.filter((task): task is ZippingTask => isTaskZippingTask(task));

export const isCopyToWorkspaceTask = (task: Pick<Task, 'type'>): task is CTWTask => task.type === 'CopyToWorkspace';

export const getCTWTasksFromTasks = (tasks: Task[]) =>
  tasks.filter((task): task is CTWTask => isCopyToWorkspaceTask(task));

export const isDuplicateBoardTask = (task: Pick<Task, 'type'>): task is DuplicateBoardTask =>
  task.type === 'DuplicateBoard';

export const isBoardDeletionTask = (task: Pick<Task, 'type'>): task is BoardDeletionTask =>
  task.type === 'BoardDeletion';

export const isLibraryDeletionTask = (task: Pick<Task, 'type'>): task is LibraryDeletionTask =>
  task.type === 'LibraryDeletion';

export const isBoardRestorationTask = (task: Pick<Task, 'type'>): task is BoardRestorationTask =>
  task.type === 'BoardRestoration';

export const isLibraryRestorationTask = (task: Pick<Task, 'type'>): task is LibraryRestorationTask =>
  task.type === 'LibraryRestoration';

export const isAssetRestorationTask = (task: Pick<Task, 'type'>): task is AssetRestorationTask =>
  task.type === 'AssetRestoration';

export const isImporterTask = (task: Pick<Task, 'type'>): task is ImporterTask => task.type === 'Importer';

export const isAssetTransformationTask = (task: Pick<Task, 'type'>): task is AssetTransformationTask =>
  task.type === 'AssetTransformation';

export const getDuplicateBoardTasksFromTasks = (tasks: Task[]) => tasks.filter(isDuplicateBoardTask);

export const getBoardDeletionTasksFromTasks = (tasks: Task[]) => tasks.filter(isBoardDeletionTask);

export const getLibraryDeletionTasksFromTasks = (tasks: Task[]) => tasks.filter(isLibraryDeletionTask);

export const getBoardRestorationTasksFromTasks = (tasks: Task[]) => tasks.filter(isBoardRestorationTask);

export const getLibraryRestorationTasksFromTasks = (tasks: Task[]) => tasks.filter(isLibraryRestorationTask);

export const getAssetRestorationTasksFromTasks = (tasks: Task[]) => tasks.filter(isAssetRestorationTask);

export const getImporterTasksFromTasks = (tasks: Task[]) => tasks.filter(isImporterTask);

export const getAssetTransformationTasksFromTasks = (tasks: Task[]) => tasks.filter(isAssetTransformationTask);

export const getAdImportDescriptionMetadata = (metadata: ImporterTask['metadata']): MetaImportDescriptor | null => {
  if (metadata?.source === 'meta' && metadata?.files?.[0]?.descriptor) {
    const descriptor = metadata.files[0].descriptor;
    try {
      const metaDescriptor = JSON.parse(descriptor) as MetaImportDescriptor;
      return metaDescriptor;
    } catch (error) {
      reportErrorToBugsnag({
        error,
        context: 'Error parsing meta descriptor',
        metadata: {
          descriptor,
        },
      });
    }
  }
  return null;
};

export const isAssetTaggingTask = (task: Pick<Task, 'type'>): task is AssetTaggingTask => task.type === 'AssetTagging';

export const getAssetTaggingTasksFromTasks = (tasks: Task[]) =>
  tasks.filter((task): task is AssetTaggingTask => isAssetTaggingTask(task));

export const isAssetCustomFieldValueUpdateTask = (task: Pick<Task, 'type'>): task is AssetCustomFieldValueUpdateTask =>
  task.type === 'AssetCustomFieldValueUpdate';

export const getAssetCustomFieldValueUpdateTasksFromTasks = (tasks: Task[]) =>
  tasks.filter((task): task is AssetCustomFieldValueUpdateTask => isAssetCustomFieldValueUpdateTask(task));

/**
 * Type guard to check if a task's metadata has an assetVersionId property
 */
export const hasAssetVersionId = <T extends { metadata: Record<string, unknown> }>(
  task: T,
  assetVersionId: string,
): task is T & { metadata: { assetVersionId: string } } =>
  'assetVersionId' in task.metadata && task.metadata.assetVersionId === assetVersionId;
